import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { format, eachWeekOfInterval, eachMonthOfInterval, endOfWeek, endOfMonth, differenceInDays, getDay, getHours } from "date-fns";
import { sv } from "date-fns/locale";
import { TrendingUp, TrendingDown } from "lucide-react";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import ExportDropdown from "./ExportDropdown";
import type { ReportDateRange } from "./ReportDateRangePicker";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import MatchiSettledRow from "@/components/admin/MatchiSettledRow";
import { DEFAULT_VAT_BY_TYPE, vatFromGross, netFromGross } from "@/lib/vatRates";

const REVENUE_TYPE_KEYS = [
  { key: "all", i18nKey: "allRevenue" },
  { key: "booking", i18nKey: "courtBooking" },
  { key: "tournament", i18nKey: "tournaments" },
  { key: "training", i18nKey: "trainingLessons" },
  { key: "membership", i18nKey: "membership" },
  { key: "punchcard", i18nKey: "punchCard" },
  { key: "credits", i18nKey: "punchCard" },
  { key: "addons", i18nKey: "addons" },
];

const TYPE_COLORS: Record<string, string> = {
  booking: "hsl(var(--primary))",
  tournament: "hsl(var(--chart-2))",
  training: "hsl(var(--chart-3))",
  membership: "hsl(var(--chart-4))",
  punchcard: "hsl(var(--chart-5))",
  credits: "hsl(142 71% 45%)",
  addons: "hsl(280 60% 55%)",
};

// Sprint D Plan A: dual-state types (legacy + _minor) on all money fields.
interface PaymentRecord {
  amount: number;
  amount_minor: number | null;
  currency: string;
  payment_method: string | null;
  payment_type: string | null;
  paid_at: string | null;
  created_at: string;
  [key: string]: unknown;
}

interface BookingRecord {
  id: string;
  court_id: string;
  start_time: string;
  price_total: number | null;
  price_total_minor: number | null;
  currency: string;
  status: string;
  [key: string]: unknown;
}

// Helper: get amount in öre via resolveMoney with safe fallback.
function pMinor(p: PaymentRecord): number {
  return resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0;
}
function bMinor(b: BookingRecord): number {
  return resolveMoney(b.price_total_minor, b.price_total, b.currency)?.valueMinor ?? 0;
}

interface Props {
  range: ReportDateRange;
  payments: PaymentRecord[];
  /** C3: MATCHi-avräknade betalningar — redan bortdragna ur `payments`. */
  matchiPayments?: PaymentRecord[];
  prevPayments: PaymentRecord[];
  compareEnabled: boolean;
  bookings: BookingRecord[];
  courts: { id: string; name: string }[];
}

function categorizePayment(p: PaymentRecord): string {
  const t = (p.payment_type || "").toLowerCase();
  if (t.includes("tournament")) return "tournament";
  if (t.includes("training") || t.includes("lesson")) return "training";
  if (t.includes("member")) return "membership";
  if (t.includes("punch")) return "punchcard";
  if (t.includes("credit")) return "credits";
  if (t.includes("addon")) return "addons";
  return "booking";
}

export default function RevenueTab({ range, payments, matchiPayments = [], prevPayments, compareEnabled, bookings, courts }: Props) {
  const { t } = useTranslation("admin");
  const [filter, setFilter] = useState("all");
  const { allMethods: allPaymentMethods } = usePaymentMethods();

  const REVENUE_TYPES = REVENUE_TYPE_KEYS.map((rt) => ({
    key: rt.key,
    label: t(`reports.revenue.types.${rt.i18nKey}`),
  }));

  const METHOD_LABELS: Record<string, string> = {
    card: t("reports.revenue.methods.card"), swish: "Swish", manual: t("reports.revenue.methods.manual"), cash: t("reports.revenue.methods.cash"),
    invoice: t("reports.revenue.methods.invoice"), credits: t("reports.revenue.methods.punchCard"), free: t("reports.revenue.methods.free"),
    wellness: t("reports.revenue.methods.wellness"), pending: t("reports.revenue.methods.pending"), stripe: t("reports.revenue.methods.card"),
  };

  const allMethodLabels = useMemo(() => {
    const base = { ...METHOD_LABELS };
    allPaymentMethods.forEach((m) => { if (m.key) base[m.key] = m.name; });
    return base;
  }, [allPaymentMethods]);

  // Revenue by payment method — Sprint D aggregates: sum in öre internally.
  const byMethod = useMemo(() => {
    const groups: Record<string, { count: number; amountMinor: number }> = {};
    for (const p of payments) {
      const method = p.payment_method || "unknown";
      if (!groups[method]) groups[method] = { count: 0, amountMinor: 0 };
      groups[method].count++;
      groups[method].amountMinor += pMinor(p);
    }
    return Object.entries(groups)
      .map(([method, data]) => ({ method, label: allMethodLabels[method] ?? method, count: data.count, amountMinor: data.amountMinor }))
      .sort((a, b) => b.amountMinor - a.amountMinor);
  }, [payments, allMethodLabels]);

  const useMonthly = differenceInDays(range.to, range.from) > 90;

  const filtered = useMemo(() => {
    if (filter === "all") return payments;
    return payments.filter((p) => categorizePayment(p) === filter);
  }, [payments, filter]);

  const prevFiltered = useMemo(() => {
    if (filter === "all") return prevPayments;
    return prevPayments.filter((p) => categorizePayment(p) === filter);
  }, [prevPayments, filter]);

  // Sprint D aggregates: sum in öre internally.
  const totalCurrentMinor = filtered.reduce((s, p) => s + pMinor(p), 0);
  // C3/BES-004: särredovisat — påverkar aldrig totalCurrentMinor eller jämförelsen.
  const matchiSettledMinor = matchiPayments.reduce((s, p) => s + pMinor(p), 0);
  const totalPrevMinor = prevFiltered.reduce((s, p) => s + pMinor(p), 0);
  const changePct = totalPrevMinor > 0 ? Math.round(((totalCurrentMinor - totalPrevMinor) / totalPrevMinor) * 100) : totalCurrentMinor > 0 ? 100 : 0;
  const changeAbsMinor = totalCurrentMinor - totalPrevMinor;

  // Stacked bar chart data
  const chartData = useMemo(() => {
    const intervals = useMonthly
      ? eachMonthOfInterval({ start: range.from, end: range.to })
      : eachWeekOfInterval({ start: range.from, end: range.to }, { weekStartsOn: 1 });

    return intervals.map((start) => {
      const end = useMonthly ? endOfMonth(start) : endOfWeek(start, { weekStartsOn: 1 });
      const label = useMonthly ? format(start, "MMM", { locale: sv }) : format(start, "d/M");
      const entry: Record<string, string | number> = { period: label };

      // Chart data still rendered in kronor for human-readable axis.
      Object.keys(TYPE_COLORS).forEach((type) => {
        entry[type] = Math.round(payments
          .filter((p) => {
            const d = new Date(p.paid_at || p.created_at);
            return d >= start && d <= end && categorizePayment(p) === type;
          })
          .reduce((s: number, p) => s + pMinor(p), 0) / 100);
      });
      return entry;
    });
  }, [payments, range, useMonthly]);

  // Breakdown table — Sprint D aggregates: sum in öre internally.
  const breakdown = useMemo(() => {
    const types = Object.keys(TYPE_COLORS);
    const totalMinor = payments.reduce((s, p) => s + pMinor(p), 0);
    return types.map((type) => {
      const items = payments.filter((p) => categorizePayment(p) === type);
      const prevItems = prevPayments.filter((p) => categorizePayment(p) === type);
      const amountMinor = items.reduce((s: number, p) => s + pMinor(p), 0);
      const prevAmountMinor = prevItems.reduce((s: number, p) => s + pMinor(p), 0);
      const change = prevAmountMinor > 0 ? Math.round(((amountMinor - prevAmountMinor) / prevAmountMinor) * 100) : amountMinor > 0 ? 100 : 0;
      const share = totalMinor > 0 ? Math.round((amountMinor / totalMinor) * 100) : 0;
      return {
        type,
        label: REVENUE_TYPES.find((t) => t.key === type)?.label || type,
        count: items.length,
        amountMinor,
        share,
        change,
      };
    }).filter((r) => r.count > 0 || r.amountMinor > 0);
  }, [payments, prevPayments]);

  const exportData = () => breakdown.map((r) => ({
    [t("reports.revenue.type")]: r.label,
    [t("reports.revenue.count")]: r.count,
    [t("reports.revenue.amount")]: Math.round(r.amountMinor / 100),  // kronor for CSV consumers
    [t("reports.revenue.share")]: r.share,
    [t("reports.revenue.change")]: r.change,
  }));

  // VAT breakdown (Momsrapport) — Sprint D aggregates: gross/net/vat in öre internally.
  const vatBreakdown = useMemo(() => {
    const groups: Record<number, { netMinor: number; vatMinor: number; grossMinor: number }> = {};
    for (const p of payments) {
      const category = categorizePayment(p);
      const vatType = category === "booking" ? "court_booking" : category;
      const vatRate = DEFAULT_VAT_BY_TYPE[vatType] ?? 25;
      const grossMinor = pMinor(p);
      const netMinor = netFromGross(grossMinor, vatRate);
      const vatMinorVal = vatFromGross(grossMinor, vatRate);
      if (!groups[vatRate]) groups[vatRate] = { netMinor: 0, vatMinor: 0, grossMinor: 0 };
      groups[vatRate].netMinor += netMinor;
      groups[vatRate].vatMinor += vatMinorVal;
      groups[vatRate].grossMinor += grossMinor;
    }
    return Object.entries(groups)
      .map(([rate, data]) => ({ rate: Number(rate), ...data }))
      .sort((a, b) => a.rate - b.rate);
  }, [payments]);

  const vatTotals = useMemo(() => {
    return vatBreakdown.reduce(
      (acc, row) => ({ netMinor: acc.netMinor + row.netMinor, vatMinor: acc.vatMinor + row.vatMinor, grossMinor: acc.grossMinor + row.grossMinor }),
      { netMinor: 0, vatMinor: 0, grossMinor: 0 }
    );
  }, [vatBreakdown]);

  // Revenue per court
  const courtNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of courts) map[c.id] = c.name;
    return map;
  }, [courts]);

  // Per-court — Sprint D aggregates: sum in öre internally; chart shows kronor.
  const perCourt = useMemo(() => {
    const groups: Record<string, number> = {};
    for (const b of bookings) {
      if (b.status === "cancelled") continue;
      const courtId = b.court_id;
      groups[courtId] = (groups[courtId] || 0) + bMinor(b);
    }
    return Object.entries(groups)
      .map(([courtId, amountMinor]) => ({
        courtId,
        name: courtNameMap[courtId] || courtId,
        amount: Math.round(amountMinor / 100),
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [bookings, courtNameMap]);

  // Prime time vs Off-peak — Sprint D aggregates: sum in öre internally.
  const primeTimeData = useMemo(() => {
    let primeTotalMinor = 0;
    let primeCount = 0;
    let offTotalMinor = 0;
    let offCount = 0;
    for (const b of bookings) {
      if (b.status === "cancelled") continue;
      const d = new Date(b.start_time);
      const day = getDay(d); // 0=Sun, 1=Mon ... 6=Sat
      const hour = getHours(d);
      const isWeekday = day >= 1 && day <= 5;
      const isPrime = isWeekday && hour >= 17 && hour < 21;
      const amountMinor = bMinor(b);
      if (isPrime) {
        primeTotalMinor += amountMinor;
        primeCount++;
      } else {
        offTotalMinor += amountMinor;
        offCount++;
      }
    }
    return { primeTotalMinor, primeCount, offTotalMinor, offCount };
  }, [bookings]);

  // Day report (Dagrapport) — Sprint D aggregates: sum in öre internally.
  const dayReport = useMemo(() => {
    const groups: Record<string, { count: number; amountMinor: number }> = {};
    for (const p of payments) {
      const date = format(new Date(p.paid_at || p.created_at), "yyyy-MM-dd");
      if (!groups[date]) groups[date] = { count: 0, amountMinor: 0 };
      groups[date].count++;
      groups[date].amountMinor += pMinor(p);
    }
    return Object.entries(groups)
      .map(([date, data]) => ({ date, count: data.count, amountMinor: data.amountMinor }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [payments]);

  const exportDayReport = () => dayReport.map((r) => ({
    [t("reports.revenue.date")]: r.date,
    [t("reports.revenue.count")]: r.count,
    [t("reports.revenue.revenue")]: Math.round(r.amountMinor / 100),  // kronor for CSV consumers
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {REVENUE_TYPES.map((t) => (
            <Badge
              key={t.key}
              variant={filter === t.key ? "default" : "outline"}
              className="cursor-pointer text-xs"
              onClick={() => setFilter(t.key)}
            >
              {t.label}
            </Badge>
          ))}
        </div>
        <ExportDropdown getData={exportData} filePrefix="intakter" />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            {/* C3/BES-004: "Totala intäkter" = Boply-intäkt. MATCHi-avräknat visas
                som egen post nedan, aldrig inbakat här. */}
            <p className="text-xs text-muted-foreground">{t("reports.revenue.totalRevenue")}</p>
            <p className="text-2xl font-bold">{formatPrice({ valueMinor: totalCurrentMinor, currency: "SEK" })}</p>
          </CardContent>
        </Card>
        {matchiSettledMinor > 0 && (
          <Card>
            <CardContent className="p-4">
              <MatchiSettledRow valueMinor={matchiSettledMinor} variant="card" />
            </CardContent>
          </Card>
        )}
        {compareEnabled && (
          <>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t("reports.revenue.change")}</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{changePct >= 0 ? "+" : ""}{changePct}%</p>
                  {changePct >= 0 ? <TrendingUp className="h-5 w-5 text-green-500" /> : <TrendingDown className="h-5 w-5 text-destructive" />}
                </div>
                <p className="text-xs text-muted-foreground">{changeAbsMinor >= 0 ? "+" : ""}{formatPrice({ valueMinor: changeAbsMinor, currency: "SEK" })}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t("reports.revenue.previousPeriod")}</p>
                <p className="text-2xl font-bold">{formatPrice({ valueMinor: totalPrevMinor, currency: "SEK" })}</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Stacked bar chart */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.revenue.revenuePerPeriod", { period: useMonthly ? t("reports.revenue.month") : t("reports.revenue.week") })}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}`} />
              <Tooltip formatter={(v: number) => formatPrice(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {Object.entries(TYPE_COLORS).map(([key, color]) => (
                <Bar key={key} dataKey={key} stackId="a" fill={color} name={REVENUE_TYPES.find((t) => t.key === key)?.label || key} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Breakdown table */}
      {breakdown.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.revenue.revenueByType")}</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("reports.revenue.type")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.transactions")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.amount")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.share")}</TableHead>
                  {compareEnabled && <TableHead className="text-right">{t("reports.revenue.change")}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {breakdown.map((r) => (
                  <TableRow key={r.type}>
                    <TableCell className="font-medium">{r.label}</TableCell>
                    <TableCell className="text-right">{r.count}</TableCell>
                    <TableCell className="text-right">{formatPrice({ valueMinor: r.amountMinor, currency: "SEK" })}</TableCell>
                    <TableCell className="text-right">{r.share}%</TableCell>
                    {compareEnabled && (
                      <TableCell className="text-right">
                        <span className={r.change >= 0 ? "text-primary" : "text-destructive"}>
                          {r.change >= 0 ? "+" : ""}{r.change}%
                        </span>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {/* Payment method breakdown */}
      {byMethod.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.revenue.byPaymentMethod")}</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="divide-y border rounded-lg">
              {byMethod.map((m) => (
                <div key={m.method} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{m.label}</span>
                    <span className="text-xs text-muted-foreground">{m.count} st</span>
                  </div>
                  <span className="font-medium">{formatPrice({ valueMinor: m.amountMinor, currency: "SEK" })}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Momsrapport */}
      {vatBreakdown.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.revenue.vatReport")}</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("reports.revenue.vatRate")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.net")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.vat")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.gross")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vatBreakdown.map((row) => (
                  <TableRow key={row.rate}>
                    <TableCell>{row.rate}%</TableCell>
                    <TableCell className="text-right">{formatPrice({ valueMinor: row.netMinor, currency: "SEK" })}</TableCell>
                    <TableCell className="text-right">{formatPrice({ valueMinor: row.vatMinor, currency: "SEK" })}</TableCell>
                    <TableCell className="text-right">{formatPrice({ valueMinor: row.grossMinor, currency: "SEK" })}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-bold">{t("reports.revenue.total")}</TableCell>
                  <TableCell className="text-right font-bold">{formatPrice({ valueMinor: vatTotals.netMinor, currency: "SEK" })}</TableCell>
                  <TableCell className="text-right font-bold">{formatPrice({ valueMinor: vatTotals.vatMinor, currency: "SEK" })}</TableCell>
                  <TableCell className="text-right font-bold">{formatPrice({ valueMinor: vatTotals.grossMinor, currency: "SEK" })}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Per bana */}
      {perCourt.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.revenue.revenuePerCourt")}</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0">
            <ResponsiveContainer width="100%" height={Math.max(200, perCourt.length * 40)}>
              <BarChart data={perCourt} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(v: number) => formatPrice(v)} />
                <Bar dataKey="amount" fill="hsl(var(--primary))" name={t("reports.revenue.revenue")} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Prime time vs Off-peak */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.revenue.primeTime")}</p>
            <p className="text-2xl font-bold">{formatPrice({ valueMinor: primeTimeData.primeTotalMinor, currency: "SEK" })}</p>
            <p className="text-xs text-muted-foreground">{primeTimeData.primeCount} {t("reports.revenue.bookings")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.revenue.offPeak")}</p>
            <p className="text-2xl font-bold">{formatPrice({ valueMinor: primeTimeData.offTotalMinor, currency: "SEK" })}</p>
            <p className="text-xs text-muted-foreground">{primeTimeData.offCount} {t("reports.revenue.bookings")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Dagrapport / kassarapport */}
      {dayReport.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">{t("reports.revenue.dayReport")}</CardTitle>
            <ExportDropdown getData={exportDayReport} filePrefix="dagrapport" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("reports.revenue.date")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.count")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue.revenue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dayReport.map((r) => (
                  <TableRow key={r.date}>
                    <TableCell>{r.date}</TableCell>
                    <TableCell className="text-right">{r.count}</TableCell>
                    <TableCell className="text-right">{formatPrice({ valueMinor: r.amountMinor, currency: "SEK" })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
